import { authedFetch, UnauthorizedError } from '@apis/auth';
import { CAD_MAX_PREVIEW_BYTES, CAD_MAX_PREVIEW_MB } from '@constants/cad';
import type { ICadTakeoffResponse } from '@interfaces/ICadTakeoff';

export class CadModelUnavailableError extends Error {
  readonly reason = 'unavailable' as const;
  constructor(message?: string) {
    super(message ?? 'CAD model is not yet available for this site');
    this.name = 'CadModelUnavailableError';
  }
}

export class CadModelTooLargeError extends Error {
  readonly reason = 'too-large' as const;
  constructor(message?: string) {
    super(message ?? 'CAD model exceeds the preview size limit');
    this.name = 'CadModelTooLargeError';
  }
}

export type FetchProgress = {
  /** Bytes received so far. */
  loaded: number;
  /** Total bytes from `Content-Length`, or `null` when the server
   *  didn't send one (e.g. chunked transfer-encoding). */
  total: number | null;
};

/** Which artifact the viewer got: a pre-converted Fragments binary (preferred)
 *  or raw IFC bytes (the fallback that the client must parse itself). */
export type CadModelSource = 'fragments' | 'ifc';

/** Result of a (possibly conditional) CAD model fetch. When the caller passed
 *  an `If-None-Match` that the server confirmed still current, `notModified` is
 *  true and there are NO bytes — the caller reuses its cached copy. Callers
 *  MUST discriminate on `notModified` before reading `bytes`. */
export type CadModelResult =
  | { notModified: true; source: CadModelSource }
  | {
      notModified: false;
      bytes: ArrayBuffer;
      source: CadModelSource;
      /** Composed (ETag + byte-length) cache key. */
      validator: string;
      /** Raw ETag header, stored so the next visit can revalidate. */
      etag: string | null;
    };

/** Bytes plus a cache validator derived from response headers + payload size
 *  (validator ties a cached conversion to this exact server artifact), OR a
 *  304 marker when the caller's `If-None-Match` matched the server's ETag. */
export type FetchedModel =
  | { notModified: true }
  | { notModified: false; bytes: ArrayBuffer; validator: string; etag: string | null };

// Shared downloader for the binary model endpoints (raw IFC and .frag). Streams
// the body to report progress, with a fail-fast size guard applied before the
// body is touched. Path-parameterised and EXPORTED so the version-scoped
// preview routes (apis/cadUpload.ts) reuse this exact streaming/progress +
// validator loop rather than duplicating it.
export async function fetchModelBytes(
  path: string,
  signal?: AbortSignal,
  onProgress?: (progress: FetchProgress) => void,
  ifNoneMatch?: string,
): Promise<FetchedModel> {
  const init: RequestInit = { signal };
  // Conditional GET: when we already hold this model, ask the server to confirm
  // it's still current (→ 304) instead of re-streaming the multi-MB body.
  if (ifNoneMatch) init.headers = { 'If-None-Match': ifNoneMatch };
  const res = await authedFetch(path, init);

  // Handle 304 BEFORE the `!res.ok` branch — 304 is not "ok" but is a success
  // here: the server validated our cached bytes, so there is no body to read
  // and the caller reuses its cache.
  if (res.status === 304) {
    return { notModified: true };
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const code: string | undefined = body?.error?.code;
    const message: string | undefined = body?.error?.message;
    if (res.status === 401) throw new UnauthorizedError();
    if (res.status === 503 && code === 'cad_model_unavailable') {
      throw new CadModelUnavailableError(message);
    }
    throw new Error(`fetch ${path} failed: ${res.status} ${message ?? ''}`);
  }

  const totalHeader = res.headers.get('Content-Length');
  const totalRaw = totalHeader === null ? NaN : Number(totalHeader);
  const total = Number.isFinite(totalRaw) && totalRaw > 0 ? totalRaw : null;

  // Cache validator: prefer a strong-ish artifact identity (ETag), fall back to
  // Last-Modified, then the empty string. Combined with the final byte length
  // so two artifacts that happen to share a header but differ in size don't
  // collide. Composed AFTER the body completes (byteLength is authoritative).
  // Raw ETag is stored verbatim for the next visit's `If-None-Match` (readable
  // now that the API exposes ETag through CORS). The composed validator adds the
  // final byte length so a header collision that changes size still busts cache.
  const etag = res.headers.get('ETag');
  const headerValidator = etag ?? res.headers.get('Last-Modified') ?? '';
  const composeValidator = (byteLength: number) => `${headerValidator}:${byteLength}`;

  // Fail-fast size guard: reject before touching the body. Streaming/parsing a
  // model this large hangs the tab, so we never start. Best-effort — a missing
  // or non-numeric Content-Length leaves `total` null and is let through rather
  // than falsely rejected. Cancel the body to stop the download immediately.
  if (total !== null && total > CAD_MAX_PREVIEW_BYTES) {
    res.body?.cancel().catch(() => {});
    const mb = (bytes: number) => Math.round(bytes / 1024 / 1024);
    throw new CadModelTooLargeError(
      `Model (${mb(total)} MB) exceeds the ${CAD_MAX_PREVIEW_MB} MB preview limit`,
    );
  }

  // Stream the body so we can report progress. Falls back to a one-shot
  // arrayBuffer() if the runtime doesn't expose a readable body (e.g.
  // some test environments).
  const reader = res.body?.getReader();
  if (!reader) {
    onProgress?.({ loaded: 0, total });
    const buf = await res.arrayBuffer();
    onProgress?.({ loaded: buf.byteLength, total });
    return { notModified: false, bytes: buf, validator: composeValidator(buf.byteLength), etag };
  }

  const chunks: Uint8Array[] = [];
  let loaded = 0;
  onProgress?.({ loaded, total });

  // The streaming loop drives the progress callback; the spread-then-set
  // concat at the end avoids a second pass for a final ArrayBuffer copy.
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      onProgress?.({ loaded, total });
    }
  }

  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return { notModified: false, bytes: out.buffer, validator: composeValidator(out.byteLength), etag };
}

/** Raw IFC bytes for a site (the client parses these with web-ifc). */
export function fetchIfcModel(
  siteId: string,
  signal?: AbortSignal,
  onProgress?: (progress: FetchProgress) => void,
  ifNoneMatch?: string,
): Promise<FetchedModel> {
  return fetchModelBytes(`/api/cad/sites/${encodeURIComponent(siteId)}/ifc`, signal, onProgress, ifNoneMatch);
}

/** Pre-converted ThatOpen Fragments (.frag) for a site — no client-side parse. */
export function fetchFragments(
  siteId: string,
  signal?: AbortSignal,
  onProgress?: (progress: FetchProgress) => void,
  ifNoneMatch?: string,
): Promise<FetchedModel> {
  return fetchModelBytes(`/api/cad/sites/${encodeURIComponent(siteId)}/fragments`, signal, onProgress, ifNoneMatch);
}

/**
 * Fetch the site's CAD model, preferring the pre-converted `.frag` and falling
 * back to raw IFC when no fragments artifact is published (503 unavailable).
 * The returned `source` tells the viewer which loader to use.
 *
 * `opts.ifNoneMatch` revalidates a cached copy, but ONLY against the endpoint
 * matching `opts.cachedSource`: a frag etag is sent to `/fragments` only, an ifc
 * etag to `/ifc` only. A stale cross-source etag would otherwise 304 a request
 * for a genuinely different artifact and suppress a real download.
 */
export async function fetchCadModel(
  siteId: string,
  signal?: AbortSignal,
  onProgress?: (progress: FetchProgress) => void,
  opts: { ifNoneMatch?: string; cachedSource?: CadModelSource } = {},
): Promise<CadModelResult> {
  const { ifNoneMatch, cachedSource } = opts;
  try {
    const frag = await fetchFragments(
      siteId,
      signal,
      onProgress,
      cachedSource === 'fragments' ? ifNoneMatch : undefined,
    );
    if (frag.notModified) return { notModified: true, source: 'fragments' };
    return { notModified: false, bytes: frag.bytes, source: 'fragments', validator: frag.validator, etag: frag.etag };
  } catch (err) {
    // Only the "no fragments published" case falls back to raw IFC — a
    // too-large / auth / network error must surface, not silently retry.
    if (err instanceof CadModelUnavailableError) {
      const ifc = await fetchIfcModel(
        siteId,
        signal,
        onProgress,
        cachedSource === 'ifc' ? ifNoneMatch : undefined,
      );
      if (ifc.notModified) return { notModified: true, source: 'ifc' };
      return { notModified: false, bytes: ifc.bytes, source: 'ifc', validator: ifc.validator, etag: ifc.etag };
    }
    throw err;
  }
}

/** One CAD presentation layer + the element localIds it contains. */
export type CadLayerEntry = { localId: number; name: string; itemIds: number[] };
export type CadLayersJson = { layers: CadLayerEntry[] };

/**
 * Shared GET-and-parse for the small JSON CAD endpoints (layers, take-off).
 * Throws `UnauthorizedError` on a 401 and `CadModelUnavailableError` on a 503
 * `cad_model_unavailable` envelope — callers should treat the latter as "no
 * CAD artifact yet" rather than a hard failure. `errorLabel` only shapes the
 * generic-failure message (e.g. "fetch cad layers failed: 500").
 */
async function fetchCadJson<T>(
  path: string,
  signal: AbortSignal | undefined,
  errorLabel: string,
): Promise<T> {
  const res = await authedFetch(path, { signal });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const code: string | undefined = body?.error?.code;
    if (res.status === 401) throw new UnauthorizedError();
    if (res.status === 503 && code === 'cad_model_unavailable') {
      throw new CadModelUnavailableError(body?.error?.message);
    }
    throw new Error(`fetch ${errorLabel} failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/**
 * Fetch + parse a `layers.json` from an arbitrary CAD layers endpoint. Shared
 * by the active-model route and the version-scoped preview route
 * (apis/cadUpload.ts). Throws `CadModelUnavailableError` when the server has no
 * layers artifact — callers should treat that as "no layers panel".
 */
export function fetchLayersAtPath(
  path: string,
  signal?: AbortSignal,
): Promise<CadLayersJson> {
  return fetchCadJson<CadLayersJson>(path, signal, 'cad layers');
}

/**
 * Precomputed layer list + membership for the fragments-mode viewer (the `.frag`
 * itself has no layer data). Throws `CadModelUnavailableError` when the server
 * has no layers artifact — callers should treat that as "no layers panel".
 */
export function fetchCadLayers(
  siteId: string,
  signal?: AbortSignal,
): Promise<CadLayersJson> {
  return fetchLayersAtPath(`/api/cad/sites/${encodeURIComponent(siteId)}/layers`, signal);
}

/**
 * Per-storey material quantity take-off for the site's active CAD model,
 * feeding the Scheduler "stock" panel. Throws `CadModelUnavailableError` when
 * the site has no active/processed CAD model yet.
 */
export function fetchCadTakeoff(
  siteId: string,
  signal?: AbortSignal,
): Promise<ICadTakeoffResponse> {
  return fetchCadJson<ICadTakeoffResponse>(
    `/api/cad/sites/${encodeURIComponent(siteId)}/takeoff`,
    signal,
    'cad takeoff',
  );
}
